import {Button, message, Popconfirm} from 'antd'
import {
    course_getStudents_Api,
    studentLink_approveLinkRequest_Api,
    studentLink_getLinkRequests_Api,
    studentLink_rejectLinkRequest_Api,
    studentLink_sendInviteBulk_Api
} from 'apis'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {StudentActionTypes} from 'views/AppLocals/Course'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {renderStudentCardItem, renderStudentTableRow} from '../functions/renderStudent'
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {RiSearchEyeLine} from "react-icons/ri";
import {BsGrid3X3GapFill, BsPersonFillCheck, BsPersonFillX, BsTable} from "react-icons/bs";
import {HiOutlineMail} from "react-icons/hi";
import {IoInformationCircleOutline} from "react-icons/io5";
import {StudentFilter} from "../components/filters/StudentFilter";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import {useStudentForm} from "../../../AppLocals/Course/CourseClass/useStudentForm";
import classNames from "classnames";
import {EntityPaginationMeta} from "../../../AppLocals/EntityList";
import {PiLinkSimpleBold, PiStudentFill} from "react-icons/pi";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import WorkspaceContext from "../../WorkspaceContext";
import PagePaginationWrapper from "../../../../modules/asyncCache/components/PagePagination/PagePaginationWrapper";
import EmptyHolder from "../../../../components/EmptyHolder";
import {renderLoading} from "../../../AppLocals/functions/renderers/renderLoading";

const RELATIONSHIP_LABELS = {
    SELF: 'self (student)',
    FATHER: 'father',
    MOTHER: 'mother',
    BROTHER: 'brother / sister',
    SISTER: 'sister',
    GUARDIAN: 'guardian',
}

const LinkRequestsPanel = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const [requests, setRequests] = useState([])
    const [loaded, setLoaded] = useState(false)

    const {handleAsyncAction: loadRequests, isLoading} = useAsyncAction({
        apiInfo: studentLink_getLinkRequests_Api,
        onSuccess: (_, meta) => {
            setRequests(meta?.response?.data ?? [])
            setLoaded(true)
        },
    })

    const {handleAsyncAction: approveReq} = useAsyncAction({
        apiInfo: studentLink_approveLinkRequest_Api,
        onSuccess: () => {
            message.success(t('approved'))
            loadRequests({owner_id, owner_type, status: 'pending'})
        },
    })

    const {handleAsyncAction: rejectReq} = useAsyncAction({
        apiInfo: studentLink_rejectLinkRequest_Api,
        onSuccess: () => {
            message.info(t('rejected'))
            loadRequests({owner_id, owner_type, status: 'pending'})
        },
    })

    React.useEffect(() => {
        loadRequests({owner_id, owner_type, status: 'pending'})
    }, [owner_id, owner_type])

    if (!loaded && isLoading) {
        return <div className="flex justify-center py-10 text-color-400 text-sm">{t('loading')}...</div>
    }

    if (loaded && requests.length === 0) {
        return (
            <div className="flex flex-col items-center gap-3 py-12">
                <PiLinkSimpleBold size={36} className="text-color-300 opacity-50"/>
                <span className="text-sm text-color-400 italic">{t('no pending link requests')}</span>
            </div>
        )
    }

    return (
        <div className="flex flex-col divide-y divide-color-50 mt-2">
            {requests.map((req, i) => (
                <div key={i} className="py-3 px-4 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <PiStudentFill size={18} className="text-indigo-400"/>
                    </div>
                    <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                        <span className="font-semibold text-sm text-color-000">
                            {req?.student?.fullname || req?.student?.name || t('unknown student')}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-color-400 italic flex-wrap">
                            <span>{t('requested by')} :</span>
                            <span className="text-color-200 font-medium not-italic">
                                {req?.requested_by?.name || t('unknown')}
                            </span>
                            <span>·</span>
                            <span>{t(RELATIONSHIP_LABELS[req?.relationship] || req?.relationship)}</span>
                            {req?.created_at && (
                                <>
                                    <span>·</span>
                                    <span>{formatSimplifyDate(req.created_at)}</span>
                                </>
                            )}
                        </div>
                        {req?.notes && (
                            <span className="text-xs text-color-400 italic mt-0.5">{req.notes}</span>
                        )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        <Button
                            size="small"
                            color="primary" variant="filled"
                            onClick={() => approveReq({user_id: req?.requested_by?.id}, {id: req?.id})}>
                            {t('approve')}
                        </Button>
                        <Button
                            size="small"
                            color="danger" variant="text"
                            onClick={() => rejectReq({}, {id: req?.id})}>
                            {t('reject')}
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    )
}

const renderGridMeta = (props) =>
    (
        <LayoutContext.Consumer>
            {({isLg}) => (
                <div className={classNames(isLg ? 'col-span-2' : 'col-span-1')}>
                    <EntityPaginationMeta
                        {...props}
                        style={{position: 'inherit'}}
                    />
                </div>
            )}
        </LayoutContext.Consumer>
    )

const Students = ({
                      owner_id,
                      owner_type,
                      filter_actions = {
                          sortBy: 'sort-by',
                          keyword: 'keyword',
                          course_id: 'course',
                          course_class_id: 'course-class',
                          sub_class_name: 'sub-class-name',
                          include_archived: 'include-archived',
                          active: 'active',
                      },
                  }) => {
    const t = useTranslate()
    const { isLg } = useContext(LayoutContext)
    const [activeTab, setActiveTab] = useState('students')
    // Bảng mặc định desktop, Thẻ mặc định mobile (yêu cầu) - chỉ set 1 LẦN lúc mount theo breakpoint
    // ban đầu, sau đó để người dùng tự chọn qua toggle, không tự đổi lại khi resize (tránh giật layout
    // ngoài ý muốn của người dùng đã bấm chọn).
    const [viewMode, setViewMode] = useState(() => isLg ? 'table' : 'card')

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const course_id = useRouteSearchParam(filter_actions.course_id)
    const course_class_id = useRouteSearchParam(filter_actions.course_class_id)
    const sub_class_name = useRouteSearchParam(filter_actions.sub_class_name)
    const include_archived = useRouteSearchParam(filter_actions.include_archived)
    const active = useRouteSearchParam(filter_actions.active)


    const {organization} = useContext(WorkspaceContext)
    const stats = organization?.workspace_settings?.courses?.statistics ?? {}
    const {
        refreshKey,
        modal,
        handleAction,
        onCancel,
    } = useStudentForm({owner_id, owner_type})

    // Gửi email mời liên kết HÀNG LOẠT cho mọi học viên đủ điều kiện trong TOÀN BỘ tổ chức (không
    // áp bộ lọc tìm kiếm/khóa/lớp hiện tại của danh sách - xem comment đầy đủ ở
    // StudentLinkController::sendInviteBulk()). BE tự bỏ qua học viên không đủ điều kiện
    // (canSendLinkInvite()), không cần FE tự lọc/chọn từng người trước.
    const {isLoading: isSendingBulkInvite, handleAsyncAction: sendInviteBulk} = useAsyncAction({
        apiInfo: studentLink_sendInviteBulk_Api,
        onSuccess: (_, meta) => {
            const {sent, skipped, failed, total} = meta?.response?.data ?? {}
            // BE giờ KHÔNG tính lần gửi khi Mail::send() thật sự lỗi (SMTP...) - trả riêng field
            // `failed` thay vì gộp vào `sent` (xem StudentLinkController::dispatchLinkInvite()).
            // Có failed thì cảnh báo (warning) thay vì success để admin biết cần gửi lại, không im
            // lặng như trước đây.
            if (failed > 0) {
                message.warning(`${t('bulk invite sent result', {sent, total})} · ${t('bulk invite failed count', {failed})}`)
            } else {
                message.success(t('bulk invite sent result', {sent, total}))
            }
        },
        onError: () => message.error(t('failed to send link invite')),
    })

    const renderContextMenu = useCallback(
        (item) => {
            const {
                course_count = 0,
                course_class_count = 0,
                grade_count = 0,
            } = item ?? {}
            // Đã ghi danh khóa/lớp hoặc đã có điểm thì không cho xóa - khớp guard ở BE
            // (StudentController::deleteStudent chặn theo CourseStudent/CourseClassStudent/
            // GradeDetail), disable trước ở đây để không cho bấm thay vì để bấm xong mới báo lỗi.
            const isInUse = course_count > 0 || course_class_count > 0 || grade_count > 0

            return createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view detail',
                        key: StudentActionTypes.STUDENT_DETAIL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: StudentActionTypes.STUDENT_UPDATE,
                    },
                    {
                        icon: <BsPersonFillCheck size={14} className={item?.active === 0 ? 'text-green-500' : 'text-color-500'}/>,
                        label: 'reactivate',
                        key: StudentActionTypes.STUDENT_ACTIVE,
                        disabled: item?.active === 1,
                    },
                    {
                        icon: <BsPersonFillX size={14} className={item?.active === 1 ? 'text-orange-400' : 'text-color-500' }/>,
                        label: 'temporarily inactive',
                        key: StudentActionTypes.STUDENT_DISABLE,
                        disabled: item?.active === 0,
                    },
                    {
                        icon: <BiTrash size={14} className={isInUse ? 'text-color-300' : 'text-primary-300'}/>,
                        label: 'delete',
                        key: StudentActionTypes.STUDENT_DELETE,
                        disabled: isInUse,
                        title: isInUse ? t('cannot delete student in use') : undefined,
                    },
                ].filter((e) => item),
                (key) => {
                    if (key === StudentActionTypes.STUDENT_DELETE && isInUse) return
                    handleAction(key, item)
                }
            )
        },
        [t]
    )

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            course_id, course_class_id,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    // Bảng: cột HỌC VIÊN (gộp giới tính + ngày sinh) | MÃ HV / MÃ THAM CHIẾU | LIÊN HỆ | THAO TÁC -
    // dùng chung TABLE_COLUMNS cho <thead> ở Wrapper, tránh lệch cột với renderStudentTableRow. Đã
    // bỏ cột "LỚP HỌC / KHÓA HỌC" theo yêu cầu (màn Quản lý Nhân sự > Học viên không cần hiện lớp/
    // khóa hiện tại ngay trong bảng - vẫn xem được qua "Chi tiết").
    const TABLE_COLUMNS = [
        {label: t('student'), className: 'text-left'},
        {label: `${t('student code')} /\n ${t('ref id')}`, className: 'text-left'},
        {label: t('contact'), className: 'text-left'},
        {label: t('actions'), className: 'text-right'},
    ]

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <PagePaginationWrapper
                apiInfo={course_getStudents_Api}
                query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
                values={{...filter_actions, keyword, sort_by: currentSort, ...(filterBy ?? {})}}
                refreshKey={refreshKey}
                renderItem={
                    viewMode === 'table'
                        ? renderStudentTableRow({translate: t, renderContextMenu, handleAction})
                        : renderStudentCardItem({translate: t, renderContextMenu, handleAction})
                }
                renderLoading={() => renderLoading()}
                renderEmpty={() => <EmptyHolder/>}
                Wrapper={({children}) => viewMode === 'table' ? (
                    <div className="mt-2 w-full overflow-x-auto rounded-xl border border-color-50">
                        <table className="w-full" style={{borderCollapse: 'collapse'}}>
                            <thead>
                                <tr className="background-200">
                                    {TABLE_COLUMNS.map((col, i) => (
                                        <th
                                            key={i}
                                            className={classNames(
                                                "px-3 py-2 text-2xs font-bold text-color-400 uppercase tracking-wider whitespace-pre-line",
                                                col.className)}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>{children}</tbody>
                        </table>
                    </div>
                ) : (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {children}
                    </div>
                )}
            />
        ),
        [owner_id, owner_type, t, renderContextMenu, handleAction, viewMode, filter_actions]
    )

    const viewModeToggle = (
        <div className="flex items-center flex-shrink-0 rounded-lg border border-color-200 overflow-hidden">
            <button
                type="button"
                title={t('card')}
                onClick={() => setViewMode('card')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors",
                    viewMode === 'card' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsGrid3X3GapFill size={14}/>
            </button>
            <button
                type="button"
                title={t('table')}
                onClick={() => setViewMode('table')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors border-l border-color-200",
                    viewMode === 'table' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsTable size={14}/>
            </button>
        </div>
    )


    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                course_id, course_class_id, sub_class_name, include_archived, active,
            })
            onSortChange(sortBy)
        },
        renderList,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        // Toggle Thẻ/Bảng đặt cạnh ô search (renderHeader) - KHÔNG đụng StudentFilter/search bar,
        // chỉ thêm 1 control mới cạnh nó qua đúng hook customization usePaginationHelper đã hỗ trợ sẵn.
        renderHeader: ([search_bar]) => (
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">{search_bar}</div>
                {viewModeToggle}
            </div>
        ),
        dependencies: [owner_id, owner_type, course_id, course_class_id, sub_class_name, include_archived, active],
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: StudentFilter,
        isToggleDefault: false,
    })

    const tabs = [
        {id: 'students', label: 'students', icon: <PiStudentFill size={14}/>},
        {id: 'link_requests', label: 'link requests', icon: <PiLinkSimpleBold size={13}/>},
    ]

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('student management')} {`(${stats?.student_count} ${t('student')})`}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('manage the list of all students across the entire workspace including personal information learning history and status')}
                    </div>
                </div>
                {activeTab === 'students' && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Gửi hàng loạt cho MỌI học viên đủ điều kiện trong tổ chức, không chỉ trang
                            đang xem - xem điều kiện đầy đủ ở banner hướng dẫn ngay dưới. Popconfirm bắt
                            buộc xác nhận vì đây là hành động gửi email thật, khó thu hồi. */}
                        <Popconfirm
                            title={t('send bulk link invite confirm title')}
                            description={t('send bulk link invite confirm description')}
                            onConfirm={() => sendInviteBulk({}, {owner_type, owner_id})}
                            okText={t('send')}
                            cancelText={t('cancel')}>
                            <Button
                                icon={<HiOutlineMail size={14}/>}
                                loading={isSendingBulkInvite}
                                className="flex items-center gap-1.5 font-semibold rounded-lg">
                                {t('send bulk link invite')}
                            </Button>
                        </Popconfirm>
                        <Button
                            color="primary" variant="solid"
                            className="px-6 py-2 font-semibold rounded-lg"
                            onClick={() => {
                                handleAction(StudentActionTypes.STUDENT_CREATE)
                            }}>
                            {t('add new student')}
                        </Button>
                    </div>
                )}
            </div>

            {activeTab === 'students' && (
                <div className="flex items-start gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-xs text-blue-700">
                    <IoInformationCircleOutline size={15} className="flex-shrink-0 mt-0.5"/>
                    <span className="tracking-wide leading-relaxed">
                        {t('link status hint')}
                    </span>
                </div>
            )}

            {/* Tab switcher */}
            <div className="flex items-center gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={classNames(
                            "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                            activeTab === tab.id
                                ? 'bg-secondary-500 text-white'
                                : 'background hover:bg-secondary-50 shadow-sm hover:shadow-md text-color-200 hover:text-secondary-600'
                        )}>
                        {tab.icon}
                        {t(tab.label)}
                    </button>
                ))}
            </div>

            {activeTab === 'students' && pager_widget}
            {activeTab === 'link_requests' && (
                <LinkRequestsPanel
                    owner_id={owner_id}
                    owner_type={owner_type}
                />
            )}
            {modal}
            <div className="h-16"/>
        </div>
    )
}


export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(Students)
